import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'resmaker_cookie_consent';

function CookieConsent({ onOpenPrivacyPolicy }) {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      accepted: true,
      analytics: true,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  const handleDecline = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      accepted: true,
      analytics: false,
      timestamp: new Date().toISOString()
    }));
    // Disable PostHog if user declines analytics
    if (window.posthog) {
      window.posthog.opt_out_capturing();
    }
    setShowBanner(false);
  };

  const handleClose = () => {
    // Treat closing as accepting essential only
    handleDecline();
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6">
      <div className="max-w-4xl mx-auto bg-white border border-slate-200 rounded-xl shadow-lg p-4 md:p-6">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <h3 className="font-heading font-semibold text-slate-900 mb-2">
              Cookie Preferences
            </h3>
            <p className="text-sm text-slate-600 mb-3">
              We use essential cookies for authentication and optional analytics cookies (PostHog) to improve our service.
              Your data is never sold. See our{' '}
              <button
                onClick={onOpenPrivacyPolicy}
                className="text-primary underline hover:text-dark"
              >
                Privacy Policy
              </button>
              {' '}for details.
            </p>
            <p className="text-xs text-slate-500 mb-4">
              Operated by Impulse Theory LLC, a California company.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleAccept}
                className="px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-dark transition-colors"
              >
                Accept All
              </button>
              <button
                onClick={handleDecline}
                className="px-4 py-2 bg-slate-100 text-slate-700 text-sm font-semibold rounded-lg hover:bg-slate-200 transition-colors"
              >
                Essential Only
              </button>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Close cookie banner"
          >
            <X size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper to check if analytics is allowed
export function isAnalyticsAllowed() {
  try {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) return false;
    const parsed = JSON.parse(consent);
    return parsed.analytics === true;
  } catch {
    return false;
  }
}

export default CookieConsent;
